import { CalendarClock, CalendarDays, Video } from "lucide-react";
import { Link } from "@/components/ui/link";
import type { DashboardTarget } from "@/lib/api/dashboard";
import { useDashboardWidget } from "@/lib/hooks/use-dashboard";
import { useT } from "@/lib/i18n/use-t";
import { useDashboardFormat } from "../use-dashboard-format";
import { T } from "./next-meetings-widget.i18n";
import { WidgetCard } from "./widget-card";
import { WidgetEmpty } from "./widget-empty";

export function NextMeetingsWidget({ target }: { target: DashboardTarget }) {
  const { data, isLoading, error } = useDashboardWidget("next_meetings", target);
  const t = useT(T);
  const { meetingDay, time } = useDashboardFormat();

  return (
    <WidgetCard
      icon={CalendarDays}
      title={t.nextMeetings}
      description={t.nextMeetingsDescription}
      isLoading={isLoading}
      error={error}
    >
      {data &&
        (data.length === 0 ? (
          <WidgetEmpty>{t.noMeetings}</WidgetEmpty>
        ) : (
          <div className="space-y-2">
            {data.map((meeting) => {
              const Icon = meeting.hasVideo ? Video : CalendarClock;
              return (
                <Link
                  key={meeting.meetingId}
                  to={`/clients/${meeting.clientId}`}
                  className="flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors hover:bg-accent/50"
                >
                  <div className="flex h-11 w-16 shrink-0 flex-col items-center justify-center rounded-lg bg-info/10 text-info">
                    <span className="text-[11px] font-medium">
                      {meetingDay(meeting.occurredAt)}
                    </span>
                    <span className="text-sm font-semibold">{time(meeting.occurredAt)}</span>
                  </div>
                  <div className="min-w-0 space-y-0.5">
                    <p className="truncate text-sm font-medium">{meeting.clientName}</p>
                    <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Icon className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">
                        {meeting.hasVideo ? `${meeting.topic} · ${t.video}` : meeting.topic}
                      </span>
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        ))}
    </WidgetCard>
  );
}
